import {roundToTwoDecimals} from '@Utils/math.util';
import {formatNumberInUSD} from '@Utils/number.util';
import type {TicketingConfig, TicketingFeeParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {TaxMode} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';

export interface TicketPriceBreakdown {
  facePrice: number; // pre-tax amount the organiser keeps (before fees)
  taxAmount: number; // 0 when there is no tax
  serviceFee: number; // PMW platform fee (P)
  processingFee: number; // payment-processing recovery (C)
  attendeePays: number; // buyer total
  payout: number; // organiser gross receipt = attendeePays − P − C
}

const PERCENT_DIVISOR = 100;
const MS_PER_DAY = 86_400_000;

/**
 * An event qualifies for payout this many days after it ends. Mirrors backend
 * `EventTicketingPayoutFacade::PAYOUT_DELAY_DAYS`.
 *
 * Exported because the breakdown cards state this number to the organiser ("Released at least N days after your event
 * ends"). Interpolating it keeps that promise tied to the code that keeps it — a literal in the copy would silently go
 * stale the day this changes.
 */
export const PAYOUT_QUALIFY_DELAY_DAYS = 7;

/**
 * Days of the month the payout cron runs (00:00 UTC). Must stay in sync with
 * `EventTicketingPayoutFacade::PAYOUT_RUN_DAYS_OF_MONTH` and the `0 0 1,15 * *` crontab entry.
 */
const PAYOUT_RUN_DAYS_OF_MONTH = [1, 15] as const;

export const fetchTicketingFeeParams = async (): Promise<TicketingFeeParams> => {
  return (await window.PMW.writeLocal('eventticketing/getTicketingFeeParams')) as TicketingFeeParams;
};

/**
 * Mirrors the real charge model (post Managed-Risk) so the preview matches checkout. The two fees ride on
 * DIFFERENT bases: the platform (P) fee on the pre-tax face (not levied on tax — legal requirement), and the
 * processing (C) fee on the tax-inclusive face + tax (Stripe bills its processing cost on the full charged
 * amount, so we recover it on the same base). No gross-up. The application fee equals P + C, so the
 * organiser's gross receipt is always `attendeePays − P − C`: exactly face + tax in pass mode, and
 * face + tax − fees in absorb mode. Tax flows to the organiser (they remit it). INCLUSIVE tax backs the
 * face out of the typed price by subtraction so the buyer pays exactly the typed amount; EXCLUSIVE adds tax
 * on top. The platform fee is clamped to the server-resolved per-ticket cap (null = uncapped); the rate and
 * cap themselves are resolved server-side — the client only applies the min.
 */
export const computeTicketPriceBreakdown = (config: Pick<TicketingConfig, 'price' | 'passFeesToBuyer' | 'taxRatePercent' | 'taxMode'>, fees: TicketingFeeParams): TicketPriceBreakdown => {
  const {price: ticketPrice, passFeesToBuyer, taxRatePercent, taxMode} = config;
  const taxRate = taxRatePercent / PERCENT_DIVISOR;
  const hasTax = taxRate > 0 && ticketPrice > 0;

  let facePrice = ticketPrice;
  let taxAmount = 0;
  if (hasTax && taxMode === TaxMode.INCLUSIVE) {
    facePrice = roundToTwoDecimals(ticketPrice / (1 + taxRate));
    taxAmount = roundToTwoDecimals(ticketPrice - facePrice); // by subtraction → buyer pays exactly the typed price
  } else if (hasTax) {
    taxAmount = roundToTwoDecimals(facePrice * taxRate);
  }

  const taxInclusive = roundToTwoDecimals(facePrice + taxAmount); // what the buyer pays for the ticket (tax included)
  // Platform fee on the pre-tax face only; processing fee on the tax-inclusive amount (matches the charge).
  const uncapped = facePrice * (fees.serviceFeePercent / PERCENT_DIVISOR) + fees.serviceFeeFixed;
  const serviceFee = roundToTwoDecimals(fees.serviceFeeCap === null ? uncapped : Math.min(uncapped, fees.serviceFeeCap));
  const processingFee = roundToTwoDecimals(taxInclusive * (fees.processingFeePercent / PERCENT_DIVISOR) + fees.processingFeeFixed);

  const attendeePays = passFeesToBuyer ? roundToTwoDecimals(taxInclusive + serviceFee + processingFee) : taxInclusive;
  const payout = roundToTwoDecimals(attendeePays - serviceFee - processingFee);

  return {facePrice, taxAmount, serviceFee, processingFee, attendeePays, payout};
};

export const formatFeePercent = (percent: number): string => {
  return `${roundToTwoDecimals(percent)}%`;
};

/**
 * A fee's rate as shown to organisers: "3.5%" on its own, or "2.9% + $0.30" when the fee also has a fixed part.
 * Shared by the wizard price preview and the dashboard earnings breakdown so both read the rate the same way.
 */
export const formatTicketFeeRate = (percent: number, fixedAmount: number): string => {
  if (fixedAmount > 0) {
    return `${formatFeePercent(percent)} + ${formatNumberInUSD(fixedAmount)}`;
  }
  return formatFeePercent(percent);
};

/**
 * The organiser's actual payout date: the next scheduled twice-monthly run (a PAYOUT_RUN_DAYS_OF_MONTH
 * day at 00:00 UTC) on or after the event qualifies (event end + PAYOUT_QUALIFY_DELAY_DAYS). Mirrors
 * `EventTicketingPayoutFacade::nextScheduledPayoutDateUnix` on the backend.
 */
export const getTicketingPayoutDate = (eventEndDateTime: Date): Date => {
  const qualifyMs = eventEndDateTime.getTime() + PAYOUT_QUALIFY_DELAY_DAYS * MS_PER_DAY;
  const qualify = new Date(qualifyMs);
  const year = qualify.getUTCFullYear();
  const month = qualify.getUTCMonth();
  const runDates: number[] = [];

  for (let monthOffset = 0; monthOffset <= 1; monthOffset++) {
    for (const dayOfMonth of PAYOUT_RUN_DAYS_OF_MONTH) {
      runDates.push(Date.UTC(year, month + monthOffset, dayOfMonth));
    }
  }
  runDates.sort((a, b) => a - b);

  return new Date(runDates.find((runMs) => runMs >= qualifyMs) ?? qualifyMs);
};

export const openTicketingPricingPage = (): void => {
  window.open(window.PMW.util.site_url('premium'), '_blank');
};
